import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UATConstants } from 'src/Common/Models/UATConstants';
import { IPersonalDetails } from 'src/manageAction/interfaces/ManageAction';
import { IActionCount, IGetDashboardActions, IAccountInfo } from '../interface/APIResponse';
import { ICustomFilterQuery, IViewCustomFilterResponse } from '../interface/CustomFilter';
import { IChartInfoResponse } from '../interface/IChartInfoResponse';
import { IRating, IUserRatingBase } from '../interface/Rating';
import { IDeleteViewPayload, IViewFolder } from '../interface/ViewFolder';
import { IWorkItem } from '../interface/WorkItem';
import { API_CONSTANTS, keepUnusedDataForConfig } from '../utils/store-constants';
import { CustomQuery } from 'src/Models/CustomQuery';
import { DropdownService } from 'src/Services/DropdownService';

export const rtkApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.WebApiUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(UATConstants.jwtTokenForApi);
      const coreAPIToken = sessionStorage.getItem(UATConstants.jwtTokenForCoreApi);
      const graphToken = sessionStorage.getItem(UATConstants.jwtTokenForGraph);

      if (token && graphToken && coreAPIToken) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('Coreapitoken', coreAPIToken);
        headers.set('Graphtoken', graphToken);
      }
      return headers;
    },
  }),
  tagTypes: ['GetViews', 'ViewFolders', 'getDropdownData', 'GetToolsName'],
  endpoints: (builder) => ({
    GetDashboard: builder.mutation<IGetDashboardActions, object>({
      query: (data) => ({
        method: 'POST',
        url: API_CONSTANTS.GetDashboard,
        body: data,
      }),
    }),
    GetViews: builder.query<IViewCustomFilterResponse, string>({
      query: () => ({
        method: 'GET',
        url: API_CONSTANTS.GetViews,
      }),
      providesTags: ['GetViews'],
      keepUnusedDataFor: keepUnusedDataForConfig,
    }),
    GetViewById: builder.query<ICustomFilterQuery, string>({
      query: (viewId) => ({
        method: 'GET',
        url: `${API_CONSTANTS.GetViewById}/${viewId}`,
      }),
      providesTags: ['GetViews'],
    }),
    GetChartInfo: builder.mutation<IChartInfoResponse, object>({
      query: (data) => ({
        method: 'POST',
        url: API_CONSTANTS.GetChartInfo,
        body: data,
      }),
    }),
    GetActionCount: builder.mutation<IActionCount, object>({
      query: (data) => ({
        method: 'POST',
        url: API_CONSTANTS.GetActionCount,
        body: data,
      }),
    }),
    GetViewsWithFolders: builder.query<IViewFolder[], void>({
      query: () => ({
        method: 'POST',
        url: API_CONSTANTS.GetViewsWithFolders,
      }),
      providesTags: ['ViewFolders'],
      keepUnusedDataFor: keepUnusedDataForConfig,
    }),
    updateCustomViewFolder: builder.mutation<ICustomFilterQuery, ICustomFilterQuery>({
      query: (body) => ({
        method: 'PUT',
        url: API_CONSTANTS.UpdateCustomQuery,
        body,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    updateAddViewToFavoritesData: builder.mutation<void, string | undefined>({
      query: (data) => ({
        method: 'PUT',
        url: `${API_CONSTANTS.AddViewToFavorites}/${data}`,
        body: data,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    updateRemoveViewFromFavoritesData: builder.mutation<void, string | undefined>({
      query: (data) => ({
        method: 'PUT',
        url: `${API_CONSTANTS.RemoveViewFromFavorites}/${data}`,
        body: data,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    createTemporaryView: builder.mutation<ICustomFilterQuery, string | undefined>({
      query: (data) => ({
        method: 'POST',
        url: `${API_CONSTANTS.CreateTemporaryView}/?MainViewId=${data}`,
      }),
    }),
    getAllDataGrid: builder.mutation<IWorkItem, object>({
      query: (data) => ({
        method: 'POST',
        url: API_CONSTANTS.GetAllData,
        body: data,
      }),
    }),
    updateMarkAsDefault: builder.mutation<void, string | undefined>({
      query: (data) => ({
        method: 'PUT',
        url: `${API_CONSTANTS.MarkViewAsDefault}/${data}`,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    deleteUserView: builder.mutation<void, IDeleteViewPayload>({
      query: (view) => ({
        method: 'DELETE',
        url: `${API_CONSTANTS.DeleteUserView}/${view.id}/${view.ViewCategory}`,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    deleteFolder: builder.mutation<unknown, string | undefined>({
      query: (folderId) => ({
        method: 'DELETE',
        url: `${API_CONSTANTS.DeleteFolder}/${folderId}`,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    saveCustomQuery: builder.mutation<ICustomFilterQuery, ICustomFilterQuery>({
      query: (body) => ({
        method: 'POST',
        url: API_CONSTANTS.SaveCustomQuery,
        body,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    createFolder: builder.mutation<IViewFolder, IViewFolder>({
      query: (body) => ({
        method: 'POST',
        url: API_CONSTANTS.CreateFolder,
        body,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    postUserRating: builder.mutation<boolean, IRating>({
      query: (userRatingItem) => ({
        method: 'POST',
        url: `${API_CONSTANTS.PostUserRating}`,
        body: userRatingItem,
      }),
    }),
    fetchRatingConfig: builder.query<number, void>({
      query: () => ({
        method: 'GET',
        url: `${API_CONSTANTS.GetRatingConfig}`,
      }),
    }),
    fetchUserRatingData: builder.query<IUserRatingBase[], string | null>({
      query: (userAlias) => ({
        method: 'GET',
        url: `${API_CONSTANTS.GetUserRating}?userAlias=${userAlias}`,
      }),
    }),
    getDropdownData: builder.query<unknown[], string>({
      query: () => ({
        method: 'GET',
        url: API_CONSTANTS.GetDropdownData,
      }),
      providesTags: ['getDropdownData'],
      keepUnusedDataFor: Infinity,
    }),
    getDropdownDataCached: builder.query({
      queryFn: async () => {
        return new Promise<{ data: any }>((resolve, reject) => {
          DropdownService.fetchDropdownData(
            (response: any) => resolve({ data: response }),
            (error: any) => reject(error)
          );
        });
      },
      keepUnusedDataFor: Infinity,
    }),

    GetToolsName: builder.query<any[], string>({
      query: () => ({
        method: 'GET',
        url: API_CONSTANTS.GetToolsName,
      }),
      providesTags: ['GetToolsName'],
    }),
    PersonDetails: builder.query<any, IPersonalDetails>({
      query: (userAlias) => ({
        method: 'GET',
        url: `${API_CONSTANTS.PersonDetails}?aliases=${userAlias}`,
      }),
    }),
    ManagersDetails: builder.query<any, string>({
      query: (userAlias) => ({
        method: 'GET',
        url: `${API_CONSTANTS.PersonDetails}?aliases=${userAlias}`,
      }),
    }),
    GetAlias: builder.query<any, string>({
      query: (userId) => ({
        method: 'GET',
        url: `${API_CONSTANTS.GetAlias}?userId=${userId}`,
      }),
    }),
    addSharedView: builder.mutation<CustomQuery, string>({
      query: (viewId) => ({
        method: 'POST',
        url: `${API_CONSTANTS.CheckAndAddUserToView}?SharedViewId=${viewId}`,
      }),
      invalidatesTags: ['GetViews', 'ViewFolders'],
    }),
    GetArtemisTags: builder.query<any, string>({
      query: () => ({
        method: 'GET',
        url: `${API_CONSTANTS.GetArtemisTags}`,
      }),
    }),
  }),
});

export const {
  useGetDashboardMutation,
  useGetViewsQuery,
  useGetViewByIdQuery,
  useGetChartInfoMutation,
  useGetActionCountMutation,
  useGetViewsWithFoldersQuery,
  useUpdateCustomViewFolderMutation,
  useUpdateAddViewToFavoritesDataMutation,
  useUpdateRemoveViewFromFavoritesDataMutation,
  useCreateTemporaryViewMutation,
  useGetAllDataGridMutation,
  useUpdateMarkAsDefaultMutation,
  useDeleteUserViewMutation,
  useDeleteFolderMutation,
  useSaveCustomQueryMutation,
  useCreateFolderMutation,
  usePostUserRatingMutation,
  useLazyFetchRatingConfigQuery,
  useLazyFetchUserRatingDataQuery,
  useGetDropdownDataQuery,
  useGetDropdownDataCachedQuery,
  useGetToolsNameQuery,
  usePersonDetailsQuery,
  useManagersDetailsQuery,
  useGetAliasQuery,
  useAddSharedViewMutation,
  useGetArtemisTagsQuery,
  useLazyGetArtemisTagsQuery,
} = rtkApi;
// Copilot change tracking: 2026-11-02 frontend-regressions: Match the manager query argument type to its existing alias-string URL and callers.